import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { DataGrid } from "@mui/x-data-grid";
import { getAdminInactiveUsers } from "../../../../../utils/services/control/adminDashboardService";
import { formatDateTime } from "../../../../../utils/formatters/date";
import ExportExcelButton from "../../../../../components/Basic/ExportExcelButton";

const exportColumns = [
  { field: "employeeId", headerName: "Employee ID" },
  { field: "fullName", headerName: "Full Name" },
  { field: "mail", headerName: "Email" },
  { field: "jobPosition", headerName: "Job Position" },
  {
    field: "lastAccess",
    headerName: "Last Access",
    valueGetter: (r) => formatDateTime(r.lastAccess, "Never"),
  },
  {
    field: "daysSinceLastAccess",
    headerName: "Days Since Last Access",
    valueGetter: (r) =>
      r.daysSinceLastAccess === null || r.daysSinceLastAccess === undefined
        ? "Never"
        : r.daysSinceLastAccess,
  },
  {
    field: "userCreateDate",
    headerName: "Account Created",
    valueGetter: (r) => formatDateTime(r.userCreateDate),
  },
  { field: "group", headerName: "Group" },
];

const groupChip = (group) => {
  if (group === "never_logged_in") {
    return <Chip size="small" label="Never logged in" color="error" />;
  }
  return (
    <Chip size="small" label="Inactive" color="warning" variant="outlined" />
  );
};

const InactiveUsersWidget = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [thresholdDays, setThresholdDays] = useState(7);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const trimmed = search.trim();
    const t = setTimeout(() => {
      if (trimmed.length === 0 || trimmed.length >= 2) {
        setDebouncedSearch(trimmed);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const data = await getAdminInactiveUsers({
          thresholdDays,
          search: debouncedSearch || undefined,
        });
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [thresholdDays, debouncedSearch]);

  const columns = [
    {
      field: "fullName",
      headerName: "Name",
      flex: 1,
      minWidth: 180,
      renderCell: ({ row }) => (
        <Box sx={{ py: 0.5 }}>
          <Typography variant="body2" fontWeight={600}>
            {row.fullName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {row.mail}
          </Typography>
        </Box>
      ),
    },
    { field: "jobPosition", headerName: "Job", width: 150 },
    {
      field: "lastAccess",
      headerName: "Last Access",
      width: 170,
      renderCell: ({ value }) => formatDateTime(value, "Never"),
    },
    {
      field: "daysSinceLastAccess",
      headerName: "Days Idle",
      width: 100,
      renderCell: ({ value }) => {
        const display =
          value === null || value === undefined ? "99+" : value;
        const color =
          value === null || value === undefined || value >= 8
            ? "error"
            : "warning";
        return (
          <Chip
            size="small"
            label={display}
            color={color}
            variant="outlined"
          />
        );
      },
    },
    {
      field: "userCreateDate",
      headerName: "Account Created",
      width: 170,
      renderCell: ({ value }) => formatDateTime(value),
    },
    {
      field: "group",
      headerName: "Group",
      width: 150,
      renderCell: ({ value }) => groupChip(value),
    },
  ];

  return (
    <Paper sx={{ p: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            Inactive Users
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Users that never logged in or have been idle for the threshold
          </Typography>
        </Box>
        <ExportExcelButton
          rows={rows}
          columns={exportColumns}
          filename="inactive_users"
        />
      </Box>

      <Box sx={{ display: "flex", gap: 1.5, mb: 1.5, flexWrap: "wrap" }}>
        <TextField
          placeholder="Search name or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: 240 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Threshold (days)</InputLabel>
          <Select
            value={thresholdDays}
            label="Threshold (days)"
            onChange={(e) => setThresholdDays(e.target.value)}
          >
            <MenuItem value={7}>7 days</MenuItem>
            <MenuItem value={14}>14 days</MenuItem>
            <MenuItem value={30}>30 days</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ height: 420 }}>
        <DataGrid
          rows={rows.map((r) => ({ id: r.employeeId, ...r }))}
          columns={columns}
          loading={loading}
          density="compact"
          hideFooterSelectedRowCount
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          sx={{ border: "none" }}
        />
      </Box>
    </Paper>
  );
};

export default InactiveUsersWidget;
